import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    MenuItem,
    Stack,
    Typography,
} from '@mui/material';
import { BaseMultiCheckbox } from 'common/components/base-checkbox-group';
import FormProvider from 'common/components/form-provider';
import React, { useEffect, useRef } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useForm } from 'react-hook-form';
import { BaseSelectField } from 'common/components/base-select-field';
import { DecimalType } from '../constants';
import { useAppDispatch } from 'common/hook';
import { setDisplay } from 'slices/dataAnalyticsDisplaySlice';
import { IListQuestionFilterResponse } from 'types/campaign';
import { useParams } from 'react-router-dom';
import {
    getListQuestionDataFilter,
    getListQuestionFilter,
} from 'services/campaign.service';
import { createStyleSheet, handleError } from 'utils/helpers';
import { setListQuestionDataFilter } from 'slices/questionSummarySlice';
import { setIsLoading } from 'slices/loadingSlice';
import { colors } from 'common/constants';

const Display: React.FC = () => {
    const dispatch = useAppDispatch();
    const campaignId = useParams<{ id: string }>().id;
    const [listQuestionsFilter, setListQuestionsFilter] =
        React.useState<IListQuestionFilterResponse[]>();
    useEffect(() => {
        const fetchListQuestions = async () => {
            try {
                const response = await getListQuestionFilter(
                    Number(campaignId),
                    'display'
                );
                if (response.success) {
                    setListQuestionsFilter(response.data);
                }
            } catch (error) {
                handleError(error);
            }
        };
        fetchListQuestions();
    }, [campaignId]);
    const methods = useForm({
        defaultValues: {
            questions: ['all'],
            displayType: [],
            decimal: DecimalType.INTEGER,
        },
    });
    const { handleSubmit, reset, setValue, watch } = methods;
    const onSubmit = handleSubmit(async (data) => {
        const objectDisplay = {
            displayType: data.displayType,
            questions: data.questions,
            decimal: data.decimal,
        };
        let questions: string[] = [];
        if (data.questions.includes('all')) {
            questions = [
                ...(listQuestionsFilter?.map((item) => {
                    return String(item.attributes.id);
                }) as string[]),
            ];
        } else {
            questions = [...data.questions];
        }
        dispatch(setDisplay(objectDisplay));
        try {
            dispatch(setIsLoading(true));
            const response = await getListQuestionDataFilter(
                Number(campaignId),
                questions,
                data.decimal
            );
            if (response.success) {
                dispatch(setListQuestionDataFilter(response.data));
                dispatch(setIsLoading(false));
            }
        } catch (error) {
            handleError(error);
            dispatch(setIsLoading(false));
        } finally {
            dispatch(setIsLoading(false));
        }
    });
    useEffect(() => {
        const defaultValues = {
            questions: listQuestionsFilter?.map((item) => {
                return String(item.attributes.id);
            }) as string[],
            displayType: ['GRAPH', 'DATA_IN_GRAPH', 'DATA_TABLE'],
            decimal: DecimalType.INTEGER,
        };
        dispatch(setDisplay(defaultValues));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        methods.reset(defaultValues as any);
        // eslint-disable-next-line
    }, [reset, dispatch, campaignId, listQuestionsFilter]);

    useEffect(() => {
        const fetchListQuestions = async () => {
            dispatch(setIsLoading(true));
            if (!listQuestionsFilter || listQuestionsFilter.length === 0)
                return;

            try {
                const response = await getListQuestionDataFilter(
                    Number(campaignId),
                    listQuestionsFilter.map((item) =>
                        String(item.attributes.id)
                    ),
                    DecimalType.INTEGER
                );
                if (response.success) {
                    dispatch(setListQuestionDataFilter(response.data));
                    dispatch(setIsLoading(false));
                }
            } catch (error) {
                handleError(error);
                dispatch(setIsLoading(false));
            } finally {
                dispatch(setIsLoading(false));
            }
        };

        fetchListQuestions();
    }, [campaignId, dispatch, listQuestionsFilter]);

    const prevQuestionsRef = useRef<string[]>([]);

    useEffect(() => {
        const currentQuestions = watch('questions') || [];
        const previousQuestions = prevQuestionsRef.current;

        const ALL = 'all';
        const allIndividualQuestions =
            listQuestionsFilter
                ?.map((item) => String(item.attributes.id))
                .filter((id) => id !== ALL) || [];
        const selectedWithoutAll = currentQuestions.filter((id) => id !== ALL);

        const hasAll = currentQuestions.includes(ALL);
        const hadAll = previousQuestions.includes(ALL);

        if (!hadAll && hasAll) {
            setValue('questions', [ALL]);
            prevQuestionsRef.current = [ALL];
            return;
        }

        if (hadAll && selectedWithoutAll.length > 0) {
            setValue('questions', selectedWithoutAll);
            prevQuestionsRef.current = selectedWithoutAll;
            return;
        }

        if (
            !hasAll &&
            selectedWithoutAll.length === allIndividualQuestions.length
        ) {
            setValue('questions', [ALL]);
            prevQuestionsRef.current = [ALL];
            return;
        }

        prevQuestionsRef.current = currentQuestions;
        // eslint-disable-next-line
    }, [watch('questions')]);
    return (
        <Box>
            <FormProvider methods={methods} onSubmit={onSubmit}>
                <Stack spacing={3}>
                    <Accordion>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="panel1-content"
                            id="panel1-header"
                        >
                            <Typography component="span">
                                Danh sách câu hỏi
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <BaseMultiCheckbox
                                name="questions"
                                sxCheckbox={styles.checkbox}
                                options={[
                                    { value: 'all', label: 'Tất cả' },
                                    ...(listQuestionsFilter ?? []).map(
                                        (item) => ({
                                            value: String(item.attributes.id),
                                            label: item.attributes.title,
                                        })
                                    ),
                                ]}
                            />
                        </AccordionDetails>
                    </Accordion>
                    <Accordion defaultExpanded>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="panel1-content"
                            id="panel1-header"
                        >
                            <Typography component="span">
                                Lựa chọn cách thức hiển thị
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <BaseMultiCheckbox
                                name="displayType"
                                sxCheckbox={styles.checkbox}
                                options={[
                                    {
                                        value: 'GRAPH',
                                        label: 'Đồ thị',
                                    },
                                    {
                                        value: 'DATA_IN_GRAPH',
                                        label: 'Dữ liệu trong đồ thị',
                                    },
                                    {
                                        value: 'DATA_TABLE',
                                        label: 'Bảng dữ liệu',
                                    },
                                ]}
                            />
                        </AccordionDetails>
                    </Accordion>
                    <Accordion defaultExpanded>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="panel1-content"
                            id="panel1-header"
                        >
                            <Typography component="span">
                                Cấu hình số thập phân
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <BaseSelectField name="decimal" size="small">
                                <MenuItem value={DecimalType.INTEGER}>
                                    0
                                </MenuItem>
                                <MenuItem value={DecimalType.ONE_DECIMAL}>
                                    0.0
                                </MenuItem>
                                <MenuItem value={DecimalType.TWO_DECIMALS}>
                                    0.00
                                </MenuItem>
                            </BaseSelectField>
                        </AccordionDetails>
                    </Accordion>
                    <Stack
                        mt={2}
                        direction="row"
                        justifyContent="flex-end"
                        gap={2}
                    >
                        <Button
                            onClick={() => reset()}
                            sx={{
                                maxWidth: '120px',
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '14px',
                                borderRadius: '40px',
                            }}
                            type="submit"
                            variant="outlined"
                        >
                            Làm mới
                        </Button>
                        <Button
                            sx={{
                                maxWidth: '120px',
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '14px',
                                borderRadius: '40px',
                            }}
                            type="submit"
                            variant="contained"
                        >
                            Áp dụng
                        </Button>
                    </Stack>
                </Stack>
            </FormProvider>
        </Box>
    );
};
const styles = createStyleSheet({
    checkbox: {
        '& .MuiFormControlLabel-label': {
            fontWeight: '500',
        },
        '&.Mui-checked + .MuiFormControlLabel-label': {
            color: colors.main,
            fontWeight: '500',
        },
    },
});
export default Display;
